import React from 'react';
import { Modal as RNModal, ModalProps, Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../theme';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Drop-in replacement for react-native's Modal that keeps content clear of
 * the notch / Dynamic Island / status bar and the home indicator / Android
 * navigation bar — React Native's Modal applies no safe-area padding itself,
 * so full-screen and page-sheet forms rendered their header under the
 * status bar and their footer buttons under the home indicator.
 *
 * Padding depends on how the modal is presented:
 * - iOS full screen (opaque, no presentationStyle / 'fullScreen'): top + bottom
 * - iOS pageSheet / formSheet: bottom only — iOS already starts the sheet
 *   below the status bar
 * - Android opaque: bottom (navigation bar when edge-to-edge), plus top only
 *   when statusBarTranslucent draws the modal under the status bar
 * - transparent overlays (dimmed backdrop + card/sheet): untouched — their
 *   backdrop is part of `children`, so padding the wrapper would leave
 *   undimmed strips; those sheets pad their own content instead.
 */
export const InsetModal: React.FC<ModalProps> = ({ children, ...props }) => {
  const insets = useSafeAreaInsets();
  // Repaint the inset bands on light/dark toggle.
  useTheme();
  const isSheet = props.presentationStyle === 'pageSheet' || props.presentationStyle === 'formSheet';

  let paddingTop = 0;
  let paddingBottom = 0;
  if (props.transparent) {
    return <RNModal {...props}>{children}</RNModal>;
  }
  if (Platform.OS === 'ios') {
    paddingTop = isSheet ? 0 : insets.top;
    paddingBottom = insets.bottom;
  } else if (Platform.OS === 'android') {
    paddingTop = props.statusBarTranslucent ? insets.top : 0;
    paddingBottom = insets.bottom;
  } else {
    paddingTop = insets.top;
    paddingBottom = insets.bottom;
  }

  return (
    <RNModal {...props}>
      {/* The padded bands show this View's background, not the modal's
          default white — theme background so dark mode has no white bars. */}
      <View style={[styles.fill, { paddingTop, paddingBottom, backgroundColor: colors.background.primary as string }]}>{children}</View>
    </RNModal>
  );
};

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
});
